import { ref, onMounted, computed, watch, unref } from 'vue';
import { useComponentI18n } from '@rdrive/i18n';
import messages from './messages/index.js';
import { useFilterContextRefresh } from '@rdrive/data-filters';
import { useChartTheme } from '@rdrive/theme';
import { downloadChartAsPNG } from '@rdrive/helper-chart-export';

export default (props, { emit }) => {
  // LOCAL composer with the warn flags off (see @rdrive/i18n): the pagination strings
  // in component.vue come from ./messages, while `t(navigationConfig[].titleKey)` below
  // is an APP key that resolves through the root composer — which is exactly what the
  // helper's default `fallbackRoot`/`inheritLocale` preserve (see README
  // "navigationConfig").
  const { t } = useComponentI18n(messages);

  // Opt-in `valueFormatter` (asset-management): the numbers on the x axis, on the bars and in the
  // tooltip. Without one a value passes through untouched — the option is what it always was.
  const formatValue = (value) => (props.valueFormatter ? props.valueFormatter(value) : value);

  const chartRef = ref(null);
  const chartOption = ref({});
  const chartHeight = ref(props.chartCardHeight);
  const currentLevel = ref(props.currentLevel);
  const currentParent = ref(null);
  const navigationState = ref({}); // Stores state for each level
  const breadcrumbPath = ref([]);
  // Index into navigationConfig of the chart's top (the root breadcrumb). Always 0 unless
  // `skipEmptyLevels` is on, where renderTopLevel() moves it to the first level with data.
  const topLevelIndex = ref(0);
  // `skipEmptyLevels` mid-drill: set by a drill click, consumed by the load that follows it
  // (the filter tick's refresh), which skips an empty level entered by that click
  let drillPending = false;

  // Pagination state
  const currentPage = ref(1);
  const itemsPerPage = ref(100); // Maximum number of bars to show at once
  const totalItems = ref(0);
  const fullDataset = ref([]); // Store the complete dataset

  // Computed properties for pagination
  const totalPages = computed(() => Math.ceil(totalItems.value / itemsPerPage.value));
  const startItem = computed(() => (currentPage.value - 1) * itemsPerPage.value + 1);
  const endItem = computed(() => Math.min(currentPage.value * itemsPerPage.value, totalItems.value));

  // Change page function
  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages.value) {
      currentPage.value = newPage;
      updateChartWithPagination();
    }
  };

  // Apply pagination to current dataset and update chart
  const updateChartWithPagination = () => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const end = Math.min(start + itemsPerPage.value, totalItems.value);
    const paginatedData = fullDataset.value.slice(start, end);
  
    // Update chart with paginated data
    updateChartOption(paginatedData, true);
  };

  // The card is capped at `chartCardHeight` and scrolls inside beyond it, like
  // BaseBoxPlotChart (Daniel, 2026-10-13: "limit the max height of the stacked bar
  // chart" — a 100-bar page grew the card past 3,000px); the title row with the
  // breadcrumbs stays pinned at its top (style.css). It grew with its content until then.
  const cardStyle = computed(() => ({
    'max-height': `${props.chartCardHeight}px`,
    'min-height': '460px',
    'overflow-y': 'auto',
    'height': 'auto',
    'display': 'flex',
    'flex-direction': 'column'
  }));

  // Initialize breadcrumb path based on navigation config
  const initializeBreadcrumbs = () => {
    const topConfig = props.navigationConfig[topLevelIndex.value];
    breadcrumbPath.value = [{
      title: t(topConfig.titleKey),
      disabled: false,
      href: '#',
      level: topConfig.level
    }];
  };

  const breadcrumbs = computed(() => {
    return breadcrumbPath.value.map((item, index) => ({
      ...item,
      disabled: index === breadcrumbPath.value.length - 1
    }));
  });

  // A deep trail overflows its box: scroll to the end so the current crumb is in view
  // (the earlier crumbs stay reachable by scrolling left)
  const breadcrumbsContainer = ref(null);
  watch(breadcrumbs, () => {
    const el = breadcrumbsContainer.value;
    if (el) el.scrollLeft = el.scrollWidth;
  }, { flush: 'post' });

  const updateChartOption = (data, isPaginated = false) => {
    // If this is the first call with the full dataset, store it and apply pagination
    if (!isPaginated) {

      // Sort data by total value - we keep sorting even for paginated data
      // to ensure consistent ordering between pages
      data.sort((a, b) => {
        const totalA = Array.isArray(a.children) ? a.children.reduce((sum, status) => sum + status.value, 0) : a.value;
        const totalB = Array.isArray(b.children) ? b.children.reduce((sum, status) => sum + status.value, 0) : b.value;
        return totalB - totalA;
      });


      fullDataset.value = [...data]; // Store full dataset
      totalItems.value = data.length;
    
      // Reset to first page when new data is loaded
      currentPage.value = 1;
    
      // If data exceeds our limit, apply pagination immediately
      if (data.length > itemsPerPage.value) {
        const paginatedData = data.slice(0, itemsPerPage.value);
        updateChartOption(paginatedData, true);
        return;
      }
    }

  

    // Constants for chart sizing and thresholds
    const BAR_HEIGHT = 25;
    const BAR_GAP = 8;
    const AXIS_MARGIN = 80;
    const MIN_CHART_HEIGHT = 80;
    const ANIMATION_THRESHOLD = 30;
  
    const categories = data.map(item => item.name);
    const seriesData = {};

    // Process data for series
    data.forEach((item, index) => {
      if (Array.isArray(item.children)) {
        item.children.forEach(status => {
          if (!seriesData[status.color]) {
            seriesData[status.color] = { 
              id: status.color,
              name: Object.keys(status.statusCounts)[0],
              stack: 'Total',
              type: 'bar',
              data: Array(categories.length).fill(0),
              itemStyle: { color: status.color },
            };
          }
          seriesData[status.color].data[index] += status.value;
        });
      }
    });

    // Calculate height before setting options
    const calculatedHeight = Math.max(
      MIN_CHART_HEIGHT,
      (categories.length * BAR_HEIGHT) + 
      ((categories.length - 1) * BAR_GAP) + 
      AXIS_MARGIN
    );

    // Update height
    chartHeight.value = calculatedHeight;
  
    // Determine if we should use animation
    const shouldAnimate = categories.length <= ANIMATION_THRESHOLD;
  
    // Prepare chart options
    const maxValue = Math.max(...Object.values(seriesData).flatMap(series => series.data), 1);
  
    // Build the chart options directly
    const newChartOptions = {
      animation: shouldAnimate,
      animationDuration: shouldAnimate ? 800 : 0,
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        confine: true,
        enterable: true,
        extraCssText: 'max-width: 320px; overflow-x: auto;',
        formatter: (params) => {
          let tooltip = '<div style="max-height: 300px; overflow-y: auto;">';
          tooltip += `<strong>${params[0].name}</strong><br/>`;
          let totalCount = 0;
          params.forEach(param => {
            if (param.value > 0) {
              const dataItem = data.find(item => item.name === param.name);
              const childSegment = dataItem?.children?.find(child => child.color === param.color);
              const statusCounts = childSegment?.statusCounts;

              if (statusCounts) {
                Object.entries(statusCounts).forEach(([statusTitle, count]) => {
                  tooltip += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
                  tooltip += `${statusTitle}: ${formatValue(count)}<br/>`;
                  totalCount += count;
                });
              } else {
                // Fallback: use param.value directly if statusCounts not found
                tooltip += `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${param.color};"></span>`;
                tooltip += `${param.seriesName}: ${formatValue(param.value)}<br/>`;
                totalCount += param.value;
              }
            }
          });
          tooltip += `<strong>${t('total')}: ${formatValue(totalCount)}</strong>`;
          tooltip += '</div>';
          return tooltip;
        }
      },
      grid: {
        left: '3%',
        right: '4%', 
        bottom: '10px',
        top: '10px',
        containLabel: true
      },
      yAxis: {
        type: 'category',
        data: categories,
        inverse: true,
        axisTick: { alignWithLabel: true },
        axisLine: { show: false },
        axisLabel: {
          margin: 10,
          color: props.chartFontColor,
          fontSize: 12,
          overflow: 'truncate',
          width: props.labelWidth
        }
      },
      xAxis: {
        type: 'value',
        // The formatter key exists only with the opt-in (no key = ECharts' own number labels)
        axisLabel: props.valueFormatter
          ? { color: props.chartFontColor, formatter: (value) => props.valueFormatter(value) }
          : { color: props.chartFontColor }
      },
      barCategoryGap: `${BAR_GAP}px`,
      barGap: '0%',
      series: Object.values(seriesData).map(series => ({
        ...series,
        barWidth: BAR_HEIGHT,
        encode: { x: 'value', y: 'category' },
        label: {
          show: true,
          position: 'inside',
          color: '#fff',
          formatter: (params) => {
            // Always show if value is large enough — unless the opt-in `labelMinWidth` is set
            // (asset-management's installation cost, where every value is > 100): then a label
            // shows only on a segment wider than it
            if (props.labelMinWidth === null && params.value > 100) return formatValue(params.value);

            // For smaller values, check if bar segment is wide enough
            if (!chartRef.value) return '';

            const chartWidth = chartRef.value.getWidth();
            if (!chartWidth || chartWidth < 100) return '';

            // Account for grid margins (left 3% + right 4% = 7% of total width)
            const gridWidth = chartWidth * 0.93;

            // Calculate the pixel width of this bar segment
            const segmentWidth = (params.value / maxValue) * gridWidth;

            // Show label only if segment is wide enough (at least 45px, or the opt-in's width)
            return segmentWidth > (props.labelMinWidth ?? 45) ? formatValue(params.value) : '';
          }
        }
      }))
    };

    // Update the chart option
    chartOption.value = newChartOptions;

    // Handle resize after a short delay to ensure DOM is updated
    setTimeout(() => {
      if (chartRef.value && chartRef.value.resize) {
        try {
          chartRef.value.resize();
        } catch (error) {
          console.warn("Error resizing chart:", error);
        }
      }
      setupLabelAreaClicks();
    }, 200);
  };

  // Make the whole label gutter clickable: map any click left of the plot area
  // to its row and drill into it (per-label hit areas are too small to rely on)
  const setupLabelAreaClicks = () => {
    const chart = chartRef.value?.chart;
    if (!chart || chart.__labelClicksBound) return;
    chart.__labelClicksBound = true;

    // Returns the page-relative row index for a point in the label gutter, or null
    const rowIndexAt = (e) => {
      let gridLeft;
      try {
        gridLeft = chart.convertToPixel({ xAxisIndex: 0 }, 0);
      } catch {
        return null;
      }
      if (typeof gridLeft !== 'number' || isNaN(gridLeft) || e.offsetX >= gridLeft) return null;

      const index = Math.round(chart.convertFromPixel({ yAxisIndex: 0 }, e.offsetY));
      const start = (currentPage.value - 1) * itemsPerPage.value;
      const pageData = fullDataset.value.slice(start, start + itemsPerPage.value);
      return index >= 0 && index < pageData.length ? index : null;
    };

    chart.getZr().on('click', (e) => {
      const index = rowIndexAt(e);
      if (index !== null) {
        handleChartClick({ dataIndex: index });
      }
    });

    chart.getZr().on('mousemove', (e) => {
      if (rowIndexAt(e) !== null) {
        chart.getZr().setCursorStyle('pointer');
      }
    });
  };

  // Handle chart item clicks for drilling down
  const handleChartClick = async (params) => {
    // Get the actual data item index accounting for pagination
    const adjustedItemIndex = (currentPage.value - 1) * itemsPerPage.value +
                             params.dataIndex;
                           
    // For drill-down, we need to work with the actual item from the full dataset
    const actualItem = fullDataset.value[adjustedItemIndex];
  
    if (!actualItem) return;
  
    // Get the actual name from the full dataset
    const actualName = actualItem.name;
  
    // Find current level index in navigation config
    const currentLevelIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
  
    // At the last level there's nowhere to drill down; instead, if the level
    // has a context updater (e.g. status), apply the clicked bar as a filter
    if (currentLevelIndex >= props.navigationConfig.length - 1) {
      const terminalUpdater = props.contextUpdaters[currentLevel.value];
      if (!terminalUpdater) return;

      navigationState.value[currentLevel.value] = {
        parentId: currentParent.value,
        selectedName: actualName
      };

      // Apply the filter first: filtersLastUpdated only ticks when the context
      // actually applied it, and a failed query must not leave the breadcrumbs
      // claiming a drill-down that never happened
      const updatedBefore = unref(props.filterContext.filtersLastUpdated);
      await terminalUpdater(actualItem.id, actualItem.parentId || null, actualItem.type || null);
      if (unref(props.filterContext.filtersLastUpdated) === updatedBefore) {
        console.error(`[${props.title}] "${actualName}" status filter was not applied — see query errors above`);
        return;
      }

      // Reflect the selection in the breadcrumbs; replace the previous terminal
      // selection instead of stacking crumbs at the same level
      const newCrumb = {
        title: actualName,
        disabled: false,
        href: '#',
        level: currentLevel.value,
        isTerminalSelection: true
      };
      const lastCrumb = breadcrumbPath.value[breadcrumbPath.value.length - 1];
      if (lastCrumb?.isTerminalSelection) {
        breadcrumbPath.value.splice(breadcrumbPath.value.length - 1, 1, newCrumb);
      } else {
        breadcrumbPath.value.push(newCrumb);
      }
      return;
    }
  
    // Get the next level in the navigation hierarchy
    const nextLevelConfig = props.navigationConfig[currentLevelIndex + 1];
  
    // Store relevant information for this level before changing
    navigationState.value[currentLevel.value] = {
      parentId: currentParent.value,
      selectedName: actualName,  // Use the actual name from full dataset
      ...(params.color ? { color: params.color } : {}),
      ...(params.seriesName ? { seriesName: params.seriesName } : {})
    };
  
    // Find the appropriate context updater for the current level
    const filterContextUpdater = props.contextUpdaters[currentLevel.value];
    const navigationConfigLevel = props.navigationConfig.find(nav => nav.level === currentLevel.value);
  
    // Update current level and parent references before updating filterContext
    currentLevel.value = nextLevelConfig.level;
    emit('update:currentLevel', nextLevelConfig.level);
    currentParent.value = navigationState.value[props.navigationConfig[currentLevelIndex].level]?.contextParam || null;
  
    // Update breadcrumb path. `level` is the level the crumb shows (it moves on if that one
    // turns out empty, skipEmptyLevels); `fromLevel` is the level whose bar was clicked, whose
    // updater a click on this crumb re-applies.
    const newBreadcrumb = {
      title: actualName || params.seriesName || t(nextLevelConfig.titleKey),
      disabled: false,
      href: '#',
      level: nextLevelConfig.level,
      fromLevel: props.navigationConfig[currentLevelIndex].level
    };
    breadcrumbPath.value.push(newBreadcrumb);

    if (filterContextUpdater && navigationConfigLevel) {
      drillPending = props.skipEmptyLevels;
      // The clicked item already carries id/parentId/type from the processed dataset —
      // names are not guaranteed unique, so never re-derive the id by name lookup
      const contextParam = actualItem.id;
      const parentIdParam = actualItem.parentId ? actualItem.parentId : null;
      const typeParam = actualItem.type ? actualItem.type : null;

      await filterContextUpdater(contextParam, parentIdParam, typeParam); //this will trigger refresh
    }
  };

  const handleBreadcrumbClick = async (e) => {
    const clickedBreadcrumb = breadcrumbPath.value.find(breadcrumb => breadcrumb.title === e.target.text);
  
    if (!clickedBreadcrumb) return;
  
    // Find the level in the navigation config
    const levelIndex = props.navigationConfig.findIndex(config => config.level === clickedBreadcrumb.level);
  
    if (levelIndex > topLevelIndex.value) {
      // Get the corresponding level config
      const levelConfig = props.navigationConfig[levelIndex];

      // Restore the state for this level if available
      const levelState = navigationState.value[levelConfig.level];
      currentLevel.value = levelConfig.level;
      emit('update:currentLevel', levelConfig.level);
      currentParent.value = levelState?.parentId || null;

      // Truncate the breadcrumb path after the clicked crumb — by its position, not its
      // nav-config index: skipEmptyLevels can skip leading levels and levels mid-drill, so
      // the crumbs are not one per level (without skipping the two are the same)
      breadcrumbPath.value = breadcrumbPath.value.slice(0, breadcrumbPath.value.indexOf(clickedBreadcrumb) + 1);

      // Get data from the level whose bar created this crumb so we know how to filter (the
      // level above, unless an empty level in between was skipped)
      const prevLevel = clickedBreadcrumb.fromLevel ?? props.navigationConfig[levelIndex - 1].level;
      const currentLevelData = props.dataProviders[prevLevel];
      const filterContextUpdater = props.contextUpdaters[prevLevel];
    
      // Apply the appropriate context update
      if (currentLevelData && filterContextUpdater && levelState?.selectedName) {
        const levelData = await currentLevelData(false /*unfiltered*/);
        const selectedItem = levelData.find(item => item.name === clickedBreadcrumb.title);
        const contextParam = selectedItem?.id;
        const parentIdParam = selectedItem?.parentId ? selectedItem.parentId : null;
        const typeParam = selectedItem?.type ? selectedItem.type : null;

        await filterContextUpdater(contextParam, parentIdParam, typeParam);
      }
    } else {
      resetToTopLevel();
    }
  };

  const resetToTopLevel = async () => {
    const resetContext = props.contextUpdaters['reset'];
    if (resetContext) {
      resetContext();
    }

    if (props.skipEmptyLevels) {
      // Back to the current top at once (so the reset's own filter tick, which may land
      // before the data does, sees the top and re-resolves it too), then re-resolve which
      // level is the top now — the reset may have brought data back to a skipped level.
      setTopLevel(topLevelIndex.value);
      currentPage.value = 1;
      await renderTopLevel();
      return;
    }

    currentLevel.value = props.navigationConfig[0].level;
    emit('update:currentLevel', props.navigationConfig[0].level);
    currentParent.value = null;
    navigationState.value = {};
    initializeBreadcrumbs();
  
    // Reset pagination state
    currentPage.value = 1;
  
    const dataProvider = props.dataProviders[currentLevel.value];
    if (dataProvider) {
      const data = await dataProvider();
      updateChartOption(data);
    }
  };

  const updateChartBasedOnCurrentLevel = async () => {
    // Reset pagination when updating based on current level
    currentPage.value = 1;
  
    // Get the appropriate data provider for the current level
    const dataProvider = props.dataProviders[currentLevel.value];
  
    if (!dataProvider) {
      console.error(`No data provider found for level: ${currentLevel.value}`);
      return;
    }

    // Only the load right after a drill click may skip; a later filter tick reloads the level
    // on screen as it is
    const afterDrill = drillPending;
    drillPending = false;

    try {
      const data = await dataProvider();
      if (data.length === 0 && afterDrill && await skipToLevelWithData()) return;
      if (data.length === 0) {
        console.warn(`[${props.title}] level "${currentLevel.value}" query returned 0 rows`);
      }
      updateChartOption(data);
    } catch (error) {
      console.error(`[${props.title}] error loading level "${currentLevel.value}":`, error);
    }
  };

  // --- skipEmptyLevels (opt-in; issue-analysis) ----------------------------------------
  // Make navigationConfig[index] the top: the level, the root breadcrumb, a clean drill state.
  const setTopLevel = (index) => {
    topLevelIndex.value = index;
    const level = props.navigationConfig[index].level;
    currentLevel.value = level;
    emit('update:currentLevel', level);
    currentParent.value = null;
    navigationState.value = {};
    initializeBreadcrumbs();
  };

  // Render the top of the hierarchy, skipping leading levels whose data is empty (e.g. a
  // project that never fills the first level's column). Nothing anywhere → the entry level,
  // empty.
  const renderTopLevel = async () => {
    currentPage.value = 1;
    let level = props.navigationConfig[0].level;
    try {
      for (let i = 0; i < props.navigationConfig.length; i++) {
        level = props.navigationConfig[i].level;
        const dataProvider = props.dataProviders[level];
        if (!dataProvider) continue;
        const data = await dataProvider();
        if (data && data.length > 0) {
          setTopLevel(i);
          updateChartOption(data);
          return;
        }
      }
      console.warn(`[${props.title}] every level returned 0 rows`);
      setTopLevel(0);
      updateChartOption([]);
    } catch (error) {
      console.error(`[${props.title}] error loading level "${level}":`, error);
    }
  };

  // A drill click entered an empty level: show the next level down that has rows instead
  // (e.g. a project whose issues never fill the issue-item column). The crumb the click
  // pushed moves with it — it keeps its title and `fromLevel`. Nothing below has rows →
  // false, and the entered level renders empty as before.
  const skipToLevelWithData = async () => {
    const enteredIndex = props.navigationConfig.findIndex(config => config.level === currentLevel.value);
    for (let i = enteredIndex + 1; i < props.navigationConfig.length; i++) {
      const level = props.navigationConfig[i].level;
      const dataProvider = props.dataProviders[level];
      if (!dataProvider) continue;
      const data = await dataProvider();
      if (data && data.length > 0) {
        currentLevel.value = level;
        emit('update:currentLevel', level);
        const crumb = breadcrumbPath.value[breadcrumbPath.value.length - 1];
        if (crumb) crumb.level = level;
        updateChartOption(data);
        return true;
      }
    }
    return false;
  };

  // The load the filter-context refresh runs: at the top, with skipEmptyLevels, re-resolve
  // which level is the top; otherwise (always, without it) reload the current level.
  const loadCurrentLevel = () => (
    props.skipEmptyLevels && breadcrumbPath.value.length <= 1
      ? renderTopLevel()
      : updateChartBasedOnCurrentLevel()
  );

  // --- PNG export (opt-in `exportFilename`) --------------------------------------------
  // The export's colours follow the page's theme (the mode only — the option's own colours are
  // the live chart's)
  const theme = useChartTheme();

  // The legend of the page on screen: per bar colour, every status title its segments count
  // (de-duplicated, first-seen order) — a series is named after only the first status of its
  // colour, so its own name would drop the rest
  const exportSeriesData = () => {
    const start = (currentPage.value - 1) * itemsPerPage.value;
    const titlesByColour = new Map();
    fullDataset.value.slice(start, start + itemsPerPage.value).forEach((row) => {
      (Array.isArray(row.children) ? row.children : []).forEach((child) => {
        if (!child?.color) return;
        if (!titlesByColour.has(child.color)) titlesByColour.set(child.color, []);
        const titles = titlesByColour.get(child.color);
        Object.keys(child.statusCounts ?? {}).forEach((title) => {
          if (!titles.includes(title)) titles.push(title);
        });
      });
    });
    return [...titlesByColour].map(([color, titles]) => ({ name: titles.join(', ') || color, titles, color }));
  };

  // The title row's download button — and callable through a template ref. The PNG's title is
  // `exportTitle` or the card title; its subtitle the breadcrumb trail once drilled.
  const downloadPng = () => {
    const chart = chartRef.value?.chart;
    if (!chart || !props.exportFilename || totalItems.value === 0) return;
    const trail = breadcrumbPath.value.map((crumb) => crumb.title);
    downloadChartAsPNG(chart, props.exportFilename, exportSeriesData(), {
      mode: theme.value.mode,
      legend: 'series',
      title: props.exportTitle || props.title,
      subtitle: trail.length > 1 ? trail.join(' › ') : '',
    });
  };

  // Watch for changes in chart font color
  watch(() => props.chartFontColor, () => {
    updateChartBasedOnCurrentLevel();
  });

  watch(() => props.currentLevel, (newVal) => {
    currentLevel.value = newVal;
  });

  // Watch for page changes to update the chart
  watch(() => currentPage.value, () => {
    updateChartWithPagination();
  });

  onMounted(() => {
    currentLevel.value = props.navigationConfig[0].level
    emit('update:currentLevel', props.navigationConfig[0].level);
    initializeBreadcrumbs();
  });

  // Owns the load lifecycle: initial load on mount (gated on dataLoaded), the
  // dataLoaded false->true load, and a reload on every filterContext change.
  // Registered after the onMounted above so breadcrumbs/level are set first.
  useFilterContextRefresh(
    computed(() => props.filterContext),
    computed(() => props.dataLoaded),
    loadCurrentLevel,
    { debounce: 0, immediate: true, requireDataLoaded: true }
  );

  return {
    // `props` itself is a template binding here: the card title renders
    // `{{ props.title }}`, and a classic component's render context exposes only
    // `$props` — so the object has to be returned under this exact name.
    props,
    t,
    // `chartRef` and `breadcrumbsContainer` are STRING template refs (`ref="…"` in
    // component.vue) — they only bind because they are returned under those exact names.
    chartRef,
    breadcrumbsContainer,
    chartOption,
    chartHeight,
    cardStyle,
    breadcrumbs,
    currentPage,
    totalPages,
    totalItems,
    startItem,
    endItem,
    changePage,
    handleChartClick,
    handleBreadcrumbClick,
    downloadPng,
  };
};
